import { useTranslation } from 'next-i18next';
import React, { useMemo, useState } from 'react';

import { useAppSelector } from '@/redux';
import { getBreadcrumbItems } from '@/utils/breadcrumbUtils';
import { formatUrl, getHrefForCategLink } from '@/utils/linkUtils';
import { removeEndSlash } from '@/utils/millesimaUtils';
import { NS } from '@/constants';

import buildLinkedData from '@/linkedData/breadcrumb-lkd';
import JsonLd from '@/components/common/JsonLd';
import MillesimaLink from '@/components/common/MillesimaLink';

import styles from './BreadcrumbMillesima.module.scss';
import { PAGES } from '@/constants/pages';

type BreadcrumbMillesimaProps = {
  breadcrumbSeo?: string[];
  url: string;
  navClassName?: string;
  className?: string;
  isLastLinkClickable?: boolean;
  homeLabel?: string;
  withPadding?: boolean;
  withMargin?: boolean;
  isSpirit?: boolean;
  textColor?: string;
  textHoverColor?: string;
  locale?: string
}

const BreadcrumbMillesima = ({
  breadcrumbSeo = [],
  url,
  navClassName = '',
  className = '',
  isLastLinkClickable,
  homeLabel = 'breadcrumbHomeLabel',
  withPadding = true,
  withMargin = true,
  isSpirit = false,
  textColor,
  textHoverColor
}: BreadcrumbMillesimaProps): React.JSX.Element => {
  const [hover, setHover] = useState(undefined);
  const { t } = useTranslation([NS.COMMON]);
  homeLabel = isSpirit ? 'breadcrumbHomeLabelSpirit' : homeLabel;
  const storeConf = useAppSelector(({ root }) => root.storeConf);

  const breadcrumb = useMemo(() =>
    getBreadcrumbItems([
      { name: t(homeLabel), url: removeEndSlash(url) },
      ...breadcrumbSeo
    ])
  , [breadcrumbSeo]);

  const json = useMemo(() => buildLinkedData(url, breadcrumb), [url, breadcrumb]);

  const olClassName = [
    styles.list,
    withPadding && styles['with-padding'],
    withMargin && styles['with-margin'],
    className
  ].filter(Boolean).join(' ');

  const getLink = (elem, key) => {
    const props = textColor && textHoverColor ? {
      onMouseEnter: () => setHover(key),
      onMouseLeave: () => setHover(undefined),
      style: { color: hover === key ? textHoverColor : textColor }
    } : {};

    return (url === PAGES.BLOG || key === 0 ? (
      <a
        href={elem.url}
        className={styles.link}
        data-rbf={`breadcrumb-link-${key}`}
        data-test-id="breadcrumb-link"
        aria-label="item"
        {...props}
      >
        {elem.name}
      </a>
    ) : (
      <MillesimaLink
        id={`breadcrumb-link-${key}`}
        customClassName={{ link: styles.link }}
        href={getHrefForCategLink(elem.url, elem.groupIdentifier, elem.isSpirit)}
        as={formatUrl(elem.url, elem.isSpirit, storeConf)}
        ariaLabel={'item'}
        innerHtml={elem.name}
        isWithoutContainer
        {...props}
      />
    ));
  };

  return (
    <>
      {json && (
        <JsonLd data={json} />
      )}
      <nav className={navClassName} aria-label="breadcrumb" data-test-id="breadcrumb-millesima" data-rbf="breadcrumb-millesima"
        sensefuel-id="breadcrumb-millesima">
        <ol className={olClassName}>
          {breadcrumb?.map((elem, key) => (
            <li key={key} className={styles.item} style={textColor && { color: textColor }} aria-current="page">
              {key === (breadcrumb.length - 1) && !isLastLinkClickable ? (
                <span data-rbf="breadcrumb-last-link" data-test-id="breadcrumb-last-link">{elem.name}</span>
              ) : (
                getLink(elem, key)
              )}
            </li>
          ))}
        </ol>
      </nav>
    </>
  );
};

export default BreadcrumbMillesima;
